import { Checkbox as HeadlessCheckbox, Field, Label, Description } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './Tooltip';

export type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  helpText?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  ariaLabel?: string;
  className?: string;
};

export const Checkbox = ({
  checked,
  onChange,
  label,
  description,
  helpText,
  disabled = false,
  indeterminate = false,
  ariaLabel,
  className = '',
}: CheckboxProps) => {
  return (
    <Field disabled={disabled} className={`flex items-center gap-3 ${className}`}>
      <HeadlessCheckbox
        checked={checked}
        onChange={onChange}
        aria-label={ariaLabel}
        className="
          group flex size-4 shrink-0 items-center justify-center rounded
          bg-white/10
          transition-all duration-200
          data-checked:bg-[var(--color-primary)]
          data-focus:outline-none data-focus:ring-1 data-focus:ring-[var(--color-primary)]/30
          data-disabled:opacity-50 data-disabled:cursor-not-allowed
          cursor-pointer
        "
      >
        {indeterminate ? (
          <svg className="stroke-white opacity-0 group-data-checked:opacity-100" viewBox="0 0 14 14" fill="none">
            <path d="M3 7H11" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg className="stroke-white opacity-0 group-data-checked:opacity-100" viewBox="0 0 14 14" fill="none">
            <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </HeadlessCheckbox>
      {(label || description) && (
        <div className="flex-1">
          {label && (
            <Label className="text-sm font-medium text-white/90 cursor-pointer">
              {label}
              {helpText && (
                <Tooltip
                  content={helpText}
                  side="right"
                  multiline
                  className="ml-1 inline-flex cursor-help"
                >
                  <QuestionMarkCircleIcon
                    className="inline h-3.5 w-3.5 text-text-tertiary"
                    aria-hidden="true"
                  />
                </Tooltip>
              )}
            </Label>
          )}
          {description && (
            <Description className="text-xs text-white/60 font-light mt-1">
              {description}
            </Description>
          )}
        </div>
      )}
    </Field>
  );
};
